import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Typography,
  IconButton,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { RootState } from '../../reducers';
import {
  setRecoveryAlertClosed,
  setRecoveryAlertMinimized,
} from './settingsSlice';

const RecoveryProgressAlert: React.FC = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { recoveryState, alertClosed, isRecoveryAlertMinimized } = useSelector(
    (state: RootState) => state.settings.recovery
  );
  const theme = useTheme();

  // Find recoveries that are actively ongoing
  const activeRecoveries = Object.entries(recoveryState).filter(
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ([_, state]) => state.status === 'ongoing'
  );

  const handleClose = () => {
    dispatch(setRecoveryAlertClosed(true));
  };

  const handleMinimize = () => {
    dispatch(setRecoveryAlertMinimized(!isRecoveryAlertMinimized));
  };

  // Only show the progress alert when on the settings/recovery page
  // Hide it in the library since the recovery card shows progress there
  const isOnRecoverySettingsPage =
    location.pathname.includes('/settings') &&
    location.pathname.includes('recovery');

  if (
    alertClosed ||
    activeRecoveries.length === 0 ||
    !isOnRecoverySettingsPage
  ) {
    return null;
  }

  return (
    <Box
      sx={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9999,
        maxWidth: {
          xs: '90%',
          sm: 450,
        },
        width: '100%',
      }}
    >
      <Alert
        severity="info"
        action={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton
              aria-label="minimize"
              color="inherit"
              size="small"
              onClick={handleMinimize}
            >
              {isRecoveryAlertMinimized ? (
                <KeyboardArrowUpIcon />
              ) : (
                <KeyboardArrowDownIcon />
              )}
            </IconButton>
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleClose}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          </Box>
        }
        sx={{
          alignItems: 'flex-start',
          position: 'relative',
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: `0 8px 32px 0 ${
            theme.palette.mode === 'dark'
              ? 'rgba(0,0,0,0.35)'
              : 'rgba(0,0,0,0.15)'
          }`,
          '& .MuiAlert-message': {
            width: '100%',
          },
          '& .MuiAlert-action': {
            position: 'absolute',
            right: 8,
            top: 8,
            padding: 0,
            margin: 0,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
          <Box sx={{ flexGrow: 1, width: '100%' }}>
            <AlertTitle sx={{ mb: isRecoveryAlertMinimized ? 0 : 2 }}>
              File Recovery Status ({activeRecoveries.length})
            </AlertTitle>
            <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
              {!isRecoveryAlertMinimized &&
                activeRecoveries.map(([fileId, state]) => (
                  <Box key={fileId} sx={{ mb: 2, '&:last-child': { mb: 0 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          flexGrow: 1,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          maxWidth: 'calc(100% - 30px)',
                        }}
                      >
                        {fileId}
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          position: 'relative',
                        }}
                      >
                        {state.status === 'success' && (
                          <CheckCircleIcon
                            sx={{ color: 'success.main', fontSize: 20 }}
                          />
                        )}
                        {state.status === 'error' && (
                          <ErrorIcon
                            sx={{ color: 'error.main', fontSize: 20 }}
                          />
                        )}
                        {state.status === 'ongoing' && (
                          <Typography variant="body2" color="text.secondary">
                            {Math.round(state.progress || 0)}%
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                ))}
            </Box>
          </Box>
        </Box>
      </Alert>
    </Box>
  );
};

export default RecoveryProgressAlert;
